import { useState } from 'react';
import {
    Card,
    Table,
    TableBody,
    TableContainer,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Box,
    Stack,
    Button,
} from '@mui/material';
import {
    TableNoData,
    TableHeadCustom,
    TablePaginationCustom,
} from 'src/components/table';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import DynamicTableRow from 'src/sections/@dashboard/DynamicTableRow';
import { fCurrencyIndo } from 'src/utils/formatNumber';
import { fDate } from 'src/utils/formatTime';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import historyAPI from 'src/api/historyAPI';
import Label from 'src/components/label';
import excelAPI from 'src/api/excelAPI';
import usePaginatedTable from 'src/hooks/usePaginatedTable';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'date', label: 'Date', align: 'center' },
    { id: 'type', label: 'Type', align: 'center' },
    { id: 'deposit', label: 'Deposit', align: 'center' },
    { id: 'deposit_after', label: 'Total deposit', align: 'center' },
];

// ----------------------------------------------------------------------

export default function DepositHistorySection({ userId }) {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // ✅ Gunakan hook universal untuk pagination, sorting, filter
    const {
        data: tableData,
        loading,
        pagination,
        setPage,
        filters,
        setFilters,
        setFilterValue,
        sort,
        handleSort,
    } = usePaginatedTable({
        apiFn: historyAPI.gethistoryByIdUser, // hanya referensi, bukan pemanggilan langsung
        params: { user_id: userId, sort_by: 'date', sort: 'desc' },
        initialFilter: {
            search: '',
            start_date: null,
            end_date: null,
        },
    });

    // 🔍 Search handler — langsung update filter (otomatis trigger fetch)
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilterValue('search', value);
    };

    // 📅 Filter date range
    const handleFilterByDate = () => {
        if (!startDate || !endDate) return;
        setFilters((prev) => ({
            ...prev,
            start_date: dayjs(startDate).format('YYYY-MM-DD'),
            end_date: dayjs(endDate).format('YYYY-MM-DD'),
        }));
    };

    // 🔄 Reset semua filter
    const handleReset = () => {
        setStartDate(null);
        setEndDate(null);
        setFilters({
            search: '',
            start_date: null,
            end_date: null,
        });
    };

    // 📤 Export Excel
    const handleExport = async () => {
        try {
            const start_date = startDate ? dayjs(startDate).format('YYYY-MM-DD') : null;
            const end_date = endDate ? dayjs(endDate).format('YYYY-MM-DD') : null;
            const response = await excelAPI.exportHistoryDepositUser(userId, start_date, end_date);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'deposit-user.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const isNotFound = !loading && tableData.length === 0;

    // ----------------------------------------------------------------------

    return (
        <>
            {/* 🔹 Header + Filter Area */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5">Deposit History</Typography>

                <Stack direction="row" spacing={1}>
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                    />
                    <Button
                        variant="contained"
                        onClick={handleFilterByDate}
                        startIcon={<Iconify icon="eva:search-outline" />}
                        disabled={!startDate || !endDate}
                    >
                        Filter
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={handleReset}>
                        Reset
                    </Button>
                </Stack>
            </Stack>

            {/* 🔍 Search + Export */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <TextField
                    fullWidth
                    placeholder="Search by type, date, or deposit..."
                    value={filters.search || ''}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleExport}
                    startIcon={<Iconify icon="eva:download-fill" />}
                >
                    Export
                </Button>
            </Stack>

            {/* 🔹 Table */}
            <Card sx={{ mt: 2 }}>
                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 300,
                                flexDirection: 'column',
                            }}
                        >
                            <CircularProgress color="primary" />
                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                Loading deposit history...
                            </Typography>
                        </Box>
                    ) : (
                        <Scrollbar>
                            <Table size="small" sx={{ minHeight: 400 }}>
                                <TableHeadCustom
                                    order={sort.orderDirection}
                                    orderBy={sort.orderBy}
                                    headLabel={TABLE_HEAD}
                                    onSort={handleSort}
                                />
                                <TableBody>
                                    {tableData.map((row) => (
                                        <DynamicTableRow
                                            key={row.id}
                                            row={{
                                                id: row.id,
                                                date: fDate(row.date),
                                                type: (
                                                    <Label color={row.type === 'in' ? 'success' : 'error'}>
                                                        {row.type}
                                                    </Label>
                                                ),
                                                deposit: fCurrencyIndo(row.deposit),
                                                deposit_after: fCurrencyIndo(row.deposit_after),
                                            }}
                                            columns={TABLE_HEAD}
                                            controlEditDelete={false}
                                        />
                                    ))}
                                    <TableNoData isNotFound={isNotFound} />
                                </TableBody>
                            </Table>
                        </Scrollbar>
                    )}
                </TableContainer>

                {/* 🔹 Pagination (fixed perPage=10) */}
                <TablePaginationCustom
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={10}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    onRowsPerPageChange={() => { }}
                    rowsPerPageOptions={[]} // sembunyikan pilihan perPage
                />
            </Card>
        </>
    );
}
